import React, { useEffect } from 'react';
import { toast } from 'sonner';
import { Trophy, Medal, Award, Crown } from 'lucide-react';
import type { RoundsResponse, RoundsRacersResponse, RoundsPicksResponse } from '@/lib/collections/rounds';
import { subscribeManyRoundsPicks } from '@/lib/collections/rounds';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { POINTS_PER_WIN, POINTS_PER_PARTICIPATION } from '@/lib/constants';

const POINTS_WIN = parseInt(POINTS_PER_WIN, 10);
const POINTS_PARTICIPATE = parseInt(POINTS_PER_PARTICIPATION, 10);

/**
 * Compute winner points using the same time-decay formula as the backend settlement.
 * Formula: 100 - 3 * floor(secondsSincePickStart / 30), floored at 55.
 * Pick phase opens at raceStartTime.
 */
function computeWinPoints(pick: RoundsPicksResponse, round: RoundsResponse): number {
  if (!pick.pickedAt || !round.raceStartTime) return POINTS_WIN;
  const secondsSincePickStart = Math.max(0, pick.pickedAt - round.raceStartTime);
  const windows = Math.floor(secondsSincePickStart / 30);
  return Math.max(55, POINTS_WIN - 3 * windows);
}

const COIN_COLORS = [
  '#ff3da8', '#fbbf24', '#34d399', '#60a5fa', '#a78bfa', '#fb7185',
];

function abbreviateAddress(addr: string): string {
  if (!addr || addr.length < 10) return addr;
  return `${addr.slice(0, 5)}…${addr.slice(-4)}`;
}

interface FinishedPanelProps {
  round: RoundsResponse;
  racers: RoundsRacersResponse[];
  userPick: RoundsPicksResponse | null;
  secondsLeft: number;
}

interface UserPodiumSlotProps {
  pick: RoundsPicksResponse;
  rank: 1 | 2 | 3;
  points: number;
  isCurrentUser: boolean;
}

const UserPodiumSlot: React.FC<UserPodiumSlotProps> = ({ pick, rank, points, isCurrentUser }) => {
  const configs = {
    1: {
      accentColor: '#fbbf24',
      labelColor: '#fde68a',
      platformColor: 'rgba(251,191,36,0.18)',
      platformBorder: 'rgba(251,191,36,0.5)',
      podiumHeight: 'h-28',
      icon: <Crown size={18} />,
      shadow: '0 0 20px rgba(251,191,36,0.35)',
    },
    2: {
      accentColor: '#94a3b8',
      labelColor: '#cbd5e1',
      platformColor: 'rgba(148,163,184,0.18)',
      platformBorder: 'rgba(148,163,184,0.4)',
      podiumHeight: 'h-20',
      icon: <Medal size={16} />,
      shadow: '0 0 12px rgba(148,163,184,0.25)',
    },
    3: {
      accentColor: '#cd7c3a',
      labelColor: '#fdba74',
      platformColor: 'rgba(205,124,58,0.15)',
      platformBorder: 'rgba(205,124,58,0.4)',
      podiumHeight: 'h-14',
      icon: <Award size={15} />,
      shadow: '0 0 8px rgba(205,124,58,0.2)',
    },
  };

  const cfg = configs[rank];

  return (
    <div className="flex flex-col items-center flex-1 min-w-0">
      {/* Avatar circle */}
      <div
        className={`rounded-full flex items-center justify-center mb-1 font-bold shrink-0 ${rank === 1 ? 'w-11 h-11 text-sm' : 'w-8 h-8 text-xs'}`}
        style={{
          background: `${cfg.accentColor}22`,
          border: `2px solid ${cfg.accentColor}`,
          boxShadow: cfg.shadow,
          color: cfg.accentColor,
        }}
      >
        {pick.userAddress.slice(0, 2).toUpperCase()}
      </div>

      {/* Address */}
      <span
        className="text-xs font-bold truncate max-w-full px-1 mb-0.5"
        style={{ color: isCurrentUser ? '#34d399' : cfg.labelColor, fontSize: rank === 1 ? '0.72rem' : '0.65rem' }}
      >
        {isCurrentUser ? 'You' : abbreviateAddress(pick.userAddress)}
      </span>

      {/* Points */}
      <span
        className="tabular-nums mb-2"
        style={{ color: cfg.accentColor, fontSize: rank === 1 ? '0.78rem' : '0.65rem', fontWeight: 700 }}
      >
        +{points} pts
      </span>

      {/* Podium block */}
      <div
        className={`w-full rounded-t-lg flex flex-col items-center justify-start pt-2 gap-1 ${cfg.podiumHeight}`}
        style={{
          background: cfg.platformColor,
          border: `1px solid ${cfg.platformBorder}`,
          borderBottom: 'none',
        }}
      >
        <span style={{ color: cfg.accentColor }}>{cfg.icon}</span>
        <span className="text-xs font-bold" style={{ color: cfg.labelColor }}>
          #{rank}
        </span>
      </div>
    </div>
  );
};

export const FinishedPanel: React.FC<FinishedPanelProps> = ({
  round,
  racers,
  userPick,
  secondsLeft,
}) => {
  // Subscribe to all picks for this round to build the user podium
  const { data: allPicks } = useRealtimeData<RoundsPicksResponse[]>(
    subscribeManyRoundsPicks,
    !!round.roundId,
    round.roundId,
  );

  // Top 3 users who picked the winning coin, sorted by earliest pick (most points)
  const winnerPicks = (allPicks ?? [])
    .filter((p) => p.coinId === round.winnerCoinId)
    .sort((a, b) => a.pickedAt - b.pickedAt)
    .slice(0, 3);

  const winner = racers.find((r) => r.coinId === round.winnerCoinId);
  const winnerIdx = racers.findIndex((r) => r.coinId === round.winnerCoinId);
  const winnerColor = winnerIdx >= 0 ? COIN_COLORS[winnerIdx % COIN_COLORS.length] : '#fbbf24';
  const userWon = userPick?.coinId === round.winnerCoinId;

  // Compute actual points awarded based on when the user picked
  const winPoints = userPick && userWon ? computeWinPoints(userPick, round) : POINTS_WIN;
  const totalPointsEarned = POINTS_PARTICIPATE + (userWon ? winPoints : 0);

  useEffect(() => {
    if (!userPick) return;
    if (userWon) {
      toast.success(`🎉 You won! +${totalPointsEarned} points!`, { duration: 5000 });
    } else {
      toast(`🤝 +${POINTS_PARTICIPATE} participation points! Better luck next time.`, { duration: 4000 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round.winnerCoinId]);

  const sortedRacers = [...racers].sort((a, b) => {
    // Always place the backend-chosen winner at rank 0 — authoritative tiebreaker
    const aIsWinner = a.coinId === round.winnerCoinId;
    const bIsWinner = b.coinId === round.winnerCoinId;
    if (aIsWinner && !bIsWinner) return -1;
    if (bIsWinner && !aIsWinner) return 1;
    return parseFloat(b.performancePct) - parseFloat(a.performancePct);
  });

  return (
    <div className="space-y-6">
      {/* Winner banner */}
      <div
        className="text-center rounded-3xl py-8 px-6 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${winnerColor}22, ${winnerColor}08)`,
          border: `3px solid ${winnerColor}66`,
        }}
      >
        {/* Confetti dots */}
        {['🎊', '🏆', '🎉', '💥', '⭐', '🎊'].map((e, i) => (
          <div
            key={i}
            className="absolute text-2xl"
            style={{
              top: `${10 + (i * 14) % 70}%`,
              left: `${5 + (i * 17) % 90}%`,
              animation: `confettiFall ${1.5 + i * 0.3}s ease-in-out ${i * 0.2}s infinite alternate`,
              opacity: 0.7,
            }}
          >
            {e}
          </div>
        ))}

        <div className="relative z-10">
          {winner?.imageUrl && (
            <img
              src={winner.imageUrl}
              alt={winner.symbol}
              className="w-20 h-20 rounded-full mx-auto mb-3 object-cover"
              style={{
                border: `4px solid ${winnerColor}`,
                boxShadow: `0 0 30px ${winnerColor}88`,
              }}
            />
          )}
          <div className="text-5xl font-bold mb-1" style={{ color: winnerColor }}>
            🏆 {winner?.symbol ?? round.winnerCoinId}
          </div>
          <div className="text-2xl font-semibold" style={{ color: '#34d399' }}>
            +{parseFloat(winner?.performancePct ?? '0').toFixed(2)}%
          </div>
          <div className="mt-2 text-sm" style={{ color: '#a78bfa' }}>
            wins Round #{round.roundId}!
          </div>
        </div>
      </div>

      {/* User result */}
      {userPick && (
        <div
          className="text-center rounded-2xl py-4 px-5 font-semibold"
          style={{
            background: userWon ? 'rgba(52,211,153,0.1)' : 'rgba(167,139,250,0.1)',
            border: `2px solid ${userWon ? 'rgba(52,211,153,0.4)' : 'rgba(167,139,250,0.3)'}`,
            color: userWon ? '#34d399' : '#a78bfa',
          }}
        >
          {userWon ? (
            <span>🎉 You picked the winner! <strong>+{totalPointsEarned} points!</strong></span>
          ) : (
            <span>💜 Good effort! <strong>+{POINTS_PARTICIPATE} participation points</strong></span>
          )}
        </div>
      )}

      {/* Final standings */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{ border: '1px solid rgba(167,139,250,0.2)' }}
      >
        <div
          className="px-4 py-2 text-xs font-bold uppercase tracking-wider"
          style={{ background: 'rgba(26,10,46,0.8)', color: '#a78bfa' }}
        >
          Final Standings
        </div>
        <div className="divide-y divide-white/5">
          {sortedRacers.map((racer, rank) => {
            const color = COIN_COLORS[racers.indexOf(racer) % COIN_COLORS.length];
            const pct = parseFloat(racer.performancePct) || 0;
            const isWinner = racer.coinId === round.winnerCoinId;
            return (
              <div
                key={racer.coinId}
                className="flex items-center gap-3 px-4 py-3"
                style={{ background: isWinner ? `${color}12` : 'transparent' }}
              >
                <span
                  className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{
                    background: rank === 0 ? '#fbbf24' : 'rgba(255,255,255,0.1)',
                    color: rank === 0 ? '#000' : 'rgba(255,255,255,0.6)',
                  }}
                >
                  {rank + 1}
                </span>
                {racer.imageUrl && (
                  <img src={racer.imageUrl} alt={racer.symbol} className="w-6 h-6 rounded-full object-cover shrink-0" />
                )}
                <span className="font-semibold flex-1" style={{ color }}>
                  ${racer.symbol}
                </span>
                {isWinner && <span>🏆</span>}
                <span
                  className="font-bold tabular-nums"
                  style={{ color: pct >= 0 ? '#34d399' : '#fb7185' }}
                >
                  {pct >= 0 ? '+' : ''}{pct.toFixed(2)}%
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top 3 Users podium */}
      {winnerPicks.length > 0 && (
        <div
          className="rounded-2xl px-4 pt-4 pb-5"
          style={{ border: '1px solid rgba(251,191,36,0.2)', background: 'rgba(26,10,46,0.4)' }}
        >
          <div className="pb-3 flex items-center gap-2">
            <Trophy size={14} style={{ color: '#fbbf24' }} />
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#fbbf24' }}>
              Top 3 Players This Round
            </span>
            {winnerPicks.length === 0 && (
              <span className="ml-auto text-xs" style={{ color: '#a78bfa' }}>No winners yet</span>
            )}
          </div>

          {/* Podium layout: 2nd | 1st | 3rd */}
          <div className="flex items-end justify-center gap-3">
            {/* 2nd place */}
            {winnerPicks.length >= 2 ? (
              <UserPodiumSlot
                pick={winnerPicks[1]}
                rank={2}
                points={computeWinPoints(winnerPicks[1], round) + POINTS_PARTICIPATE}
                isCurrentUser={winnerPicks[1].userAddress === userPick?.userAddress}
              />
            ) : <div className="flex-1" />}

            {/* 1st place — tallest */}
            <UserPodiumSlot
              pick={winnerPicks[0]}
              rank={1}
              points={computeWinPoints(winnerPicks[0], round) + POINTS_PARTICIPATE}
              isCurrentUser={winnerPicks[0].userAddress === userPick?.userAddress}
            />

            {/* 3rd place */}
            {winnerPicks.length >= 3 ? (
              <UserPodiumSlot
                pick={winnerPicks[2]}
                rank={3}
                points={computeWinPoints(winnerPicks[2], round) + POINTS_PARTICIPATE}
                isCurrentUser={winnerPicks[2].userAddress === userPick?.userAddress}
              />
            ) : <div className="flex-1" />}
          </div>
        </div>
      )}

      {/* Next round countdown */}
      <div className="text-center text-sm" style={{ color: '#a78bfa' }}>
        {secondsLeft > 0 ? (
          <span>⏰ Next race starting in <strong className="text-white">{secondsLeft}s</strong></span>
        ) : (
          <span>🔄 Starting now...</span>
        )}
      </div>

      <style>{`
        @keyframes confettiFall {
          from { transform: translateY(0) rotate(0deg); }
          to { transform: translateY(10px) rotate(20deg); }
        }
      `}</style>
    </div>
  );
};
